import React, { Fragment, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import BackToTop from 'app/products/components/backToTop/intersection';
import OutOfStockNotificationModal from 'app/commonComponents/outOfStockNotificationModal/outOfStockNotificationModal';
import { HeroBadgeContainer, PromoBadgeContainer } from 'app/commonComponents/productBadge/heroPromoBadge';
import NextDayDeliveryCountDownTimer from 'app/cmscomponents/deliveryCountdown/NextDayDeliveryCountDownTimer';
import GeneralInfo from './generalInfo/generalInfoContainer';
import ColorSwatches from './colorSwatches/colorSwatchesContainer';
import SizeSelector from './sizeSelector/sizeSelectorContainer';
import AddProduct from './addProduct/addProductContainer';
import DeliveryOptions from './deliveryOptions/deliveryOptionsContainer';
import MediaViewer from './mediaViewer/mediaViewerContainer';
import StickyAddProductWidget from './stickyAddProduct/stickyAddToBagWidget';
import StickyTopBar from './stickyTopBar/stickyTopBar';
import Breadcrumbs from './breadcrumbs/breadcrumbsContainer';
import BVCurations from './bvCurations/bvCurationsContainer';
import UpperProductSpecific from './upperProductSpecific/upperProductSpecificContainer';
import CrossSell from './crossSell/crossSellContainer';
import LowerProductSpecific from './lowerProductSpecific/lowerProductSpecificContainer';
import ProductFeatures from './detailsAccordion/productFeatures';
import Summary from './detailsAccordion/summary';
import ResponsivePortal from '../commonComponents/customPortal/customPortal';
import { isMultiVariantProduct } from './productDetailsUtils';
import './product-details.scss';

function ProductDetailsApp({ setProductDetailsRef, productDetails }) {
  const { productNotFoundContent, heroBadges, promoBadges, productType, productCode } = productDetails;

  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isSelectSizeErrorVisible, setIsSelectSizeErrorVisible] = useState(false);

  const [breadcrumbsNodes, setBreadcrumbsNodes] = useState({
    mobile: null,
    desktop: null,
  });

  const mainAddProductRef = useRef(null);

  useEffect(() => {
    setBreadcrumbsNodes({
      mobile: document.getElementById('breadcrumbsForMobile'),
      desktop: document.getElementById('breadcrumbsForDesktop'),
    });
  }, [productCode]);

  const heroBadgesElements = heroBadges
    ? // eslint-disable-next-line react/no-array-index-key
      heroBadges.map((badge, index) => <HeroBadgeContainer key={index} badge={badge} />)
    : [];

  const promoBadgesElements = promoBadges
    ? // eslint-disable-next-line react/no-array-index-key
      promoBadges.map((badge, index) => <PromoBadgeContainer key={index} badge={badge} />)
    : [];

  const closeNotificationModal = () => setIsNotificationModalOpen(false);

  const openNotificationModal = () => setIsNotificationModalOpen(true);

  return !isEmpty(productNotFoundContent) ? (
    <Fragment>
      <div className="row">
        <div className="columns">
          <div dangerouslySetInnerHTML={{ __html: productNotFoundContent }} />
          <CrossSell />
        </div>
      </div>
    </Fragment>
  ) : (
    <Fragment>
      <div className="pdp__top-fold">
        <div className="flex-row flex-row--aligned">
          <div className="flex-row__media-viewer">
            <MediaViewer />
          </div>
          <div className="flex-row__details">
            <div className="pdp__details-holder" ref={setProductDetailsRef}>
              <GeneralInfo heroBadgeElements={heroBadgesElements} promoBadgeElements={promoBadgesElements} />
              <ColorSwatches />
              <div className="next-day-delivery--mobile">
                <NextDayDeliveryCountDownTimer />
              </div>
              {isMultiVariantProduct(productType) && <SizeSelector onNotifyAction={openNotificationModal} />}
              <div ref={mainAddProductRef}>
                <AddProduct
                  onNotifyAction={openNotificationModal}
                  isSelectSizeErrorVisible={isSelectSizeErrorVisible}
                />
              </div>
              <div className="next-day-delivery--desktop">
                <NextDayDeliveryCountDownTimer />
              </div>

              <DeliveryOptions />
              <OutOfStockNotificationModal
                isModalOpen={isNotificationModalOpen}
                toggleModal={closeNotificationModal}
                productDetails={productDetails}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="hide-for-large-up">
        <StickyAddProductWidget
          isNotificationModalOpen={isNotificationModalOpen}
          mainCtaRef={mainAddProductRef}
          onNotifyAction={openNotificationModal}
          onSelectSizeRequired={() => setIsSelectSizeErrorVisible(true)}
        />
      </div>
      <StickyTopBar mainCtaRef={mainAddProductRef} onNotifyAction={openNotificationModal} />
      <ResponsivePortal component={<Summary />} portalRootId="product-summary" />
      <ResponsivePortal component={<ProductFeatures />} portalRootId="product-features" />

      {breadcrumbsNodes.mobile &&
        createPortal(
          <div className="hide-for-large-up">
            <Breadcrumbs />
          </div>,
          breadcrumbsNodes.mobile
        )}

      {breadcrumbsNodes.desktop &&
        createPortal(
          <div className="show-for-large-up">
            <Breadcrumbs />
          </div>,
          breadcrumbsNodes.desktop
        )}

      <BVCurations />
      <UpperProductSpecific />
      <CrossSell />
      <LowerProductSpecific />
      <BackToTop selector=".product-desc-wrapper" />
    </Fragment>
  );
}

ProductDetailsApp.propTypes = {
  setProductDetailsRef: PropTypes.func.isRequired,
  productDetails: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.any])).isRequired,
};

export default ProductDetailsApp;
